/**
 * HMC Events Date Utilities
 *
 * Centralized date parsing and formatting to avoid timezone bugs.
 *
 * IMPORTANT: JavaScript's `new Date('2025-04-09')` interprets date-only strings
 * as UTC midnight, which causes them to display as the previous day in western
 * timezones (e.g., April 9 UTC = April 8 CST). All date parsing should use
 * these utilities to ensure consistent local date handling.
 */

/**
 * Parse a date string (YYYY-MM-DD) as a local date to avoid timezone shifts.
 *
 * @param dateStr - Date string in YYYY-MM-DD format (e.g., "2025-04-09")
 * @returns Date object in local timezone, or null if invalid
 *
 * @example
 * // In Houston (UTC-6):
 * new Date('2025-04-09')  // ❌ Returns April 8, 6:00 PM (UTC midnight = local prev day)
 * parseLocalDate('2025-04-09')  // ✅ Returns April 9, 12:00 AM local time
 */
export function parseLocalDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;

  try {
    // Handle YYYY-MM-DD format by parsing components
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        // new Date(year, monthIndex, day) creates a local date
        return new Date(year, month - 1, day);
      }
    }
    // Fallback for other formats - this may still have timezone issues
    // but at least we tried the safe path first
    return new Date(dateStr);
  } catch {
    return null;
  }
}

/**
 * Format a date string for display with full weekday, month, day, year.
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted string like "Wednesday, April 9, 2025"
 */
export function formatEventDate(dateStr: string | null | undefined): string {
  const date = parseLocalDate(dateStr);
  if (!date) return 'Date TBD';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date string for compact display (short month, day, year).
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted string like "Apr 9, 2025"
 */
export function formatEventDateShort(dateStr: string | null | undefined): string {
  const date = parseLocalDate(dateStr);
  if (!date) return 'TBD';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string for table display (MM/DD/YYYY).
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted string like "04/09/2025"
 */
export function formatEventDateTable(dateStr: string | null | undefined): string {
  const date = parseLocalDate(dateStr);
  if (!date) return '';

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

/**
 * Get the day number from a date string.
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Day number as string (e.g., "9")
 */
export function getEventDay(dateStr: string | null | undefined): string {
  const date = parseLocalDate(dateStr);
  if (!date) return '';
  return String(date.getDate());
}

/**
 * Get the short month name from a date string.
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Short month name (e.g., "Apr")
 */
export function getEventMonth(dateStr: string | null | undefined): string {
  const date = parseLocalDate(dateStr);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short' });
}

/**
 * Get both month and day for card display.
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Object with day and month strings
 */
export function getEventDateParts(dateStr: string | null | undefined): { day: string; month: string } {
  const date = parseLocalDate(dateStr);
  if (!date) return { day: '', month: '' };

  return {
    day: String(date.getDate()),
    month: date.toLocaleDateString('en-US', { month: 'short' }),
  };
}
